import React, { useState, useEffect } from 'react';
import { API } from '@/api';
import { SITE_ID } from '@/assets/meta/meta';
import { Modal } from '@/Components/Common/Tailwind';
import { Badge } from '@/Components/ui/badge';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { Skeleton } from '@/Components/ui/skeleton';
import { Facility, FacilityImage } from '@/api/type';
import { SectionHeader } from './SectionHeader';

interface FacilityCardProps {
    facility: Facility;
    onClick: () => void;
}

function FacilityCard({ facility, onClick }: FacilityCardProps) {
    return (
        <div
            className="bg-white rounded-2xl overflow-hidden cursor-pointer shadow-[0_1px_2px_rgba(0,0,0,0.04),0_6px_16px_-4px_rgba(0,0,0,0.08)] hover:shadow-[0_2px_4px_rgba(0,0,0,0.05),0_12px_28px_-6px_rgba(0,0,0,0.12)] transition-[box-shadow,transform] duration-200 active:scale-[0.96]"
            onClick={onClick}
        >
            {facility.image && (
                <div className="relative aspect-[4/3]">
                    <img
                        src={facility.image}
                        alt={facility.name}
                        className="w-full h-full object-cover outline outline-offset-1 outline-black/10"
                    />
                </div>
            )}
            <div className="p-3">
                <h3 className="font-bold text-[14px] text-gray-900 line-clamp-1">{facility.name}</h3>
                {facility.description && (
                    <p className="text-[11px] text-gray-700 mt-1 line-clamp-2 leading-relaxed text-pretty">{facility.description}</p>
                )}
            </div>
        </div>
    );
}

interface FacilityDetailModalProps {
    facility: Facility;
    onClose: () => void;
}

function FacilityDetailModal({ facility, onClose }: FacilityDetailModalProps) {
    const [activeImageIdx, setActiveImageIdx] = useState(0);
    const images = facility.detail_images || [];

    return (
        <Modal isOpen={!!facility} toggle={onClose} variant="full" className="gap-0 border-0 bg-white">
            <div className="flex min-h-full flex-col">
                <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex items-center justify-center w-8 h-8 rounded-full text-gray-700 hover:bg-gray-100"
                        aria-label="Back"
                    >
                        <ChevronLeft size={18} />
                    </button>
                    <h2 className="text-base font-semibold text-gray-900 text-balance">{facility.name}</h2>
                </div>

                <div className="flex-1 overflow-y-auto pb-8">
                    {/* Image gallery */}
                    {images.length > 0 ? (
                        <div className="relative aspect-4/3 bg-gray-100">
                            <img
                                src={images[activeImageIdx].image}
                                className="w-full h-full object-cover"
                                alt={images[activeImageIdx].alt_text || facility.name}
                            />
                            {images.length > 1 && (
                                <>
                                    <button
                                        onClick={() => setActiveImageIdx((prev) => (prev - 1 + images.length) % images.length)}
                                        className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/40 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-black/60 transition-colors active:scale-[0.96]"
                                    >
                                        <ChevronLeft size={22} />
                                    </button>
                                    <button
                                        onClick={() => setActiveImageIdx((prev) => (prev + 1) % images.length)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/40 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-black/60 transition-colors active:scale-[0.96]"
                                    >
                                        <ChevronRight size={22} />
                                    </button>
                                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1">
                                        {images.map((_: FacilityImage, idx: number) => (
                                            <div
                                                key={idx}
                                                className={`h-1.5 rounded-full transition-[width,background-color] duration-300 ${idx === activeImageIdx ? 'w-4 bg-white' : 'w-1.5 bg-white/50'}`}
                                            />
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    ) : facility.image && (
                        <div className="aspect-[4/3] bg-gray-100">
                            <img src={facility.image} className="w-full h-full object-cover" alt={facility.name} />
                        </div>
                    )}

                    <div className="p-5 space-y-6">
                        {/* Description */}
                        {facility.description && (
                            <div>
                                <p className="text-[14px] text-gray-700 leading-relaxed whitespace-pre-wrap text-pretty">{facility.description}</p>
                            </div>
                        )}

                        {/* Include Info */}
                        {facility.include_info && facility.include_info.length > 0 && (
                            <div className="bg-gray-50 rounded-2xl p-4">
                                <h3 className="font-bold text-[13px] text-gray-900 mb-3 flex items-center gap-2">
                                    <span className="w-1.5 h-4 bg-cyan rounded-full" />
                                    포함 안내
                                </h3>
                                <ul className="space-y-2">
                                    {facility.include_info.map((item: string, idx: number) => (
                                        <li key={idx} className="text-[12px] text-gray-600 flex items-start gap-2">
                                            <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 shrink-0" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Using Info */}
                        {facility.using_info && facility.using_info.length > 0 && (
                            <div>
                                <h3 className="font-bold text-[13px] text-gray-900 mb-3 flex items-center gap-2">
                                    <span className="w-1.5 h-4 bg-green-500 rounded-full" />
                                    이용 안내
                                </h3>
                                <div className="space-y-3">
                                    {facility.using_info.map((item: string, idx: number) => (
                                        <div key={idx} className="flex gap-3">
                                            <span className="text-[11px] font-black text-green-600 bg-green-50 w-5 h-5 rounded-full flex items-center justify-center shrink-0">
                                                {idx + 1}
                                            </span>
                                            <p className="text-[12px] text-gray-600 leading-relaxed text-pretty">{item}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Caution Info */}
                        {facility.caution_info && facility.caution_info.length > 0 && (
                            <div className="border-t pt-5">
                                <h3 className="font-bold text-[13px] text-red-600 mb-3 flex items-center gap-2">
                                    <span className="w-1.5 h-4 bg-red-500 rounded-full" />
                                    유의 사항
                                </h3>
                                <ul className="space-y-2">
                                    {facility.caution_info.map((item: string, idx: number) => (
                                        <li key={idx} className="text-[12px] text-gray-600 flex items-start gap-2">
                                            <span className="mt-1.5 text-red-400">※</span>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
}

interface FacilitySectionProps {
    siteId: number;
    enabled: boolean;
}

export default function FacilitySection({ siteId, enabled }: FacilitySectionProps) {
    const [facilities, setFacilities] = useState<Facility[]>([]);
    const [selectedFacility, setSelectedFacility] = useState<Facility | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!enabled) return;

        setIsLoading(true);
        API.get(`/sites/facilities/?site=${siteId}`)
            .then(res => {
                setFacilities(Array.isArray(res.data) ? res.data : []);
            })
            .catch(console.error)
            .finally(() => setIsLoading(false));
    }, [siteId, enabled]);

    if (!enabled || (!isLoading && !facilities.length)) return null;

    return (
        <section className="p-6 pb-8">
            <SectionHeader
                title={
                    <>
                        시설 안내
                        <Badge variant="secondary" className="bg-gray-200 text-gray-700 border-none font-bold text-[10px] tabular-nums">
                            {facilities.length}
                        </Badge>
                    </>
                }
            />

            <div className="grid grid-cols-2 gap-4">
                {isLoading ? (
                    [1, 2, 3, 4].map(i => (
                        <div key={i} className="space-y-2">
                            <Skeleton className="aspect-4/3 w-full rounded-2xl" />
                            <Skeleton className="h-4 w-2/3" />
                            <Skeleton className="h-3 w-full" />
                        </div>
                    ))
                ) : (
                    facilities.map(facility => (
                        <FacilityCard
                            key={facility.id}
                            facility={facility}
                            onClick={() => setSelectedFacility(facility)}
                        />
                    ))
                )}
            </div>

            {selectedFacility && (
                <FacilityDetailModal
                    facility={selectedFacility}
                    onClose={() => setSelectedFacility(null)}
                />
            )}
        </section>
    );
}
